import { useRouter } from 'next/router';
import React from 'react';
import { z } from 'zod';

import type AnalyticsAggInterval from '@poseidon/types/pg-gen/analyticsAggInterval';
import { omit, omitBy } from '@poseidon/shared-utils';

import { CHART_VALUE_MODES } from '~/components/aiUsageChartSeries';
import { isAiUsagePeriod } from '~/components/aiUsageFormat';
import { GROUP_BYS } from '~/components/aiUsageGroupBy';
import { onboardingTourNameSchema } from '~/components/Onboarding/onboarding.model';
import { SEND_EVENTS } from '~/components/SendsTodayStrip.utils';
import { env } from '~/env';

const strToIntOrUndefined = (str: string) => {
  try {
    const num = parseInt(str, 10);
    return !isNaN(num) ? num : undefined;
  } catch {
    return undefined;
  }
};

const strToBoolOrUndefined = (str?: string) => {
  if (str == null) return undefined;
  return Boolean(str);
};
const strToArrayOrUndefined = (str?: string | string[]) => {
  if (str == null) return undefined;
  if (typeof str === 'string') return str.split(',');
  else return str;
};

export const researchSectionSchema = z.enum(['contact', 'org', 'news']);
export type ResearchSectionParam = z.infer<typeof researchSectionSchema>;

export const campaignStateSchema = z.enum([
  'active',
  'paused',
  'finished',
  'interrupted',
  'action-required',
  'responded',
  'deleted',
  'pending-review',
]);
export type CampaignState = z.infer<typeof campaignStateSchema>;

export const contactSectionSchema = z.enum(['history', 'interact']);
export type ContactSectionParam = z.infer<typeof contactSectionSchema>;

export const queryFilterSchema = z.enum([
  'userId',
  'order',
  'templateId',
  'states',
  'connectedUserId',
  'stepsRunCount',
  'response',
  'tag',
]);
export type QueryFilterParam = z.infer<typeof queryFilterSchema>;

export const pipelineModelSchema = z.enum(['contact', 'org']);
export const listBoardSchema = z.enum(['list', 'board']);
export const ascDescOrder = z.enum(['asc', 'desc']);

export const operationValueSchema = z.enum(['==', '<=', '>=']);
export type OperationValueType = z.infer<typeof operationValueSchema>;

// 'audience' = the Audience & Loaders tab (plan group E): the campaign's audience, loaders and screening.
export const templateUsersSchema = z.enum(['template', 'audience', 'users', 'people', 'staging']);
export type TemplateUsersParam = z.infer<typeof templateUsersSchema>;

// Campaign Switch detail page: which pane the segmented control shows.
export const switchDisplaySchema = z.enum(['config', 'candidates']);
export type SwitchDisplayParam = z.infer<typeof switchDisplaySchema>;

// Contact interact tab: campaign-step density — `minimal` collapses the
// cadence to a compact row of step icons, `expanded` shows the full step
// cards. Mirrors the Minimal/Expanded switch on the campaign-switch graph.
export const interactDisplaySchema = z.enum(['minimal', 'expanded']);
export type InteractDisplayParam = z.infer<typeof interactDisplaySchema>;

// Interact tab view for a contact in a sequence: `list` (the history feed, then the steps — page scroll) or
// `graph` (the whole tab as one pan/zoom canvas: history flowing into the sequence graph).
export const sequenceViewSchema = z.enum(['list', 'graph']);

export const candidateStatusSchema = z.enum([
  'pending',
  'researching',
  'research_failed',
  'gathering_sources',
  'synthesizing',
  'synthesize_failed',
  'filter_failed',
  'personalizing',
  'personalize_failed',
  'rate_limited',
  'account_disconnected',
  'awaiting_capacity',
  'retrying',
  'pending_review',
  'rejected',
  'promoted',
  'removed',
]);
export type CandidateStatusParam = z.infer<typeof candidateStatusSchema>;

/** Where a candidate came from: the auto-loader, or a human (CSV, Sales Nav URL, contacts, a switch). */
export const candidateSourceSchema = z.enum(['auto_loaded', 'human_added']);
export type CandidateSourceParam = z.infer<typeof candidateSourceSchema>;

/** People nobody has judged yet (`none`), or people who carry a verdict (`has`). */
export const candidateVerdictSchema = z.enum(['none', 'has']);
export type CandidateVerdictParam = z.infer<typeof candidateVerdictSchema>;

/** Who decided about a candidate: the AI's answer, a person's correction, the loader's admission, a rule. */
export const candidateDecisionSchema = z.enum([
  'ai_fit',
  'ai_not_fit',
  'ai_unsure',
  'person_approved',
  'person_rejected',
  'auto_admitted',
  'rule_rejected',
]);
export type CandidateDecisionParam = z.infer<typeof candidateDecisionSchema>;

export const templateSubject = z.enum(['subject', 'body2', 'body', 'from', 'to', 'cc', 'bcc']);
export type TemplateSubjectParam = z.infer<typeof templateSubject>;

export const everRespondSchema = z.enum(['ever-responded', 'never-responded', 'reached']);
export type EverRespondParam = z.infer<typeof everRespondSchema>;

export const analyticsListSchema = z.enum(['create', 'analyze', 'engage']);
export type AnalyticsListParam = z.infer<typeof analyticsListSchema>;

export const postLeadsTypeSchema = z.enum(['reactions', 'comments', 'reposts']);
export type PostLeadsTypeParam = z.infer<typeof postLeadsTypeSchema>;

export const postTypeSchema = z.enum(['data', 'leads']);
export type PostTypeParam = z.infer<typeof postTypeSchema>;

export const replyFilterSchema = z.enum(['message', 'inmail', 'email']);
export type ReplyFilterParam = z.infer<typeof replyFilterSchema>;

export const queueFilterSchema = z.enum([
  'waiting_for_user',
  'approved',
  'sending',
  'finished',
  'errored',
  'rejected',
  'stuck',
]);

/** Mirrors the server's `CANDIDATE_LIST_SORTS` (cadenceTemplate.candidateList). */
export const candidateSortSchema = z.enum(['newest', 'oldest', 'name', 'fit', 'respond']);
export type CandidateSortParam = z.infer<typeof candidateSortSchema>;

export const searchParamsSchema = z.object({
  themeMode: z.enum(['DARK', 'LIGHT', 'AUTO']).optional(),
  q: z.string().optional(),
  sharingQuery: z.string().optional(),
  name: z.string().optional(),
  id: z.string().optional(),
  graphId: z.string().optional(),
  cadenceId: z.string().optional(),
  stageId: z.string().optional(),
  stageIds: z.string().or(z.array(z.string())).transform(strToArrayOrUndefined).optional(),
  signalBuckets: z.string().or(z.array(z.string())).transform(strToArrayOrUndefined).optional(),
  hubspotId: z.string().optional(),
  contactHubspotStatus: z.enum(['error', 'incomplete_contact', 'unsure_match', 'success']).optional(),
  duplicateId: z.string().optional(),
  connectedUserId: z.string().optional(),
  orgId: z.string().optional(),
  dial: z.string().optional(),
  // Meeting booked detail params for mid-funnel report
  mbUserId: z.string().optional(),
  mbTeamId: z.string().transform(strToIntOrUndefined).optional(),
  add: z.string().transform(strToBoolOrUndefined).optional(),
  limit: z.string().transform(strToIntOrUndefined).optional(),
  offset: z.string().transform(strToIntOrUndefined).optional(),
  // Superadmin /superadmin/email-health filters — eh* prefixed so the page's four sections never
  // collide with the global q/limit/offset keys; each paginated section owns its own offset key.
  ehQ: z.string().optional(),
  ehDns: z.enum(['pending', 'verified', 'auth_failed']).optional(),
  ehPlacement: z.enum(['untested', 'pending', 'pass', 'warn', 'fail', 'inconclusive']).optional(),
  ehProblems: z.string().transform(strToBoolOrUndefined).optional(),
  ehHealth: z.enum(['healthy', 'warning', 'paused', 'validating']).optional(),
  ehPauseReason: z.enum(['dns_auth', 'bounce_rate', 'spam_placement']).optional(),
  ehDomain: z.string().optional(),
  ehSender: z.string().optional(),
  ehKinds: z.string().or(z.array(z.string())).transform(strToArrayOrUndefined).optional(),
  domainsOffset: z.string().transform(strToIntOrUndefined).optional(),
  sendersOffset: z.string().transform(strToIntOrUndefined).optional(),
  eventsOffset: z.string().transform(strToIntOrUndefined).optional(),
  type: z.string().optional(),
  afterPageId: z.string().optional(),
  beforePageId: z.string().optional(),
  detailType: z.string().optional(),
  interactDisplay: interactDisplaySchema.optional(),
  sequenceView: sequenceViewSchema.optional(),
  showOnly: z.string().optional(),
  // /sentToday channel filter: one of the sidebar Today meter's four send channels.
  sendEvent: z.enum(SEND_EVENTS).optional(),
  userId: z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .optional(),
  // Campaign-owner filter: scopes results to contacts whose active campaign
  // (active_cadence) is owned by this user. Distinct from `userId`, which scopes
  // by who is connected to the contact.
  cadenceUserId: z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .optional(),
  // teamId is used as a path param in dynamic routes like [teamId]/[userId]
  teamId: z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .optional(),
  templateId: z.string().optional(),
  // Campaign Switch: public id used as the [switchId] path param on
  // /switches/[switchId], and which pane the segmented control shows.
  switchId: z.string().optional(),
  switchDisplay: switchDisplaySchema.optional(),
  display: listBoardSchema.optional(),
  pipelineModel: pipelineModelSchema.optional(),
  templateDisplay: templateUsersSchema.optional(),
  // Touch-graph view density on the template page — 'minimal' (compact icon nodes,
  // with the enrolled-contact count badges shown) vs 'expanded' (full touch cards).
  // Absent = expanded.
  graphView: z.enum(['minimal', 'expanded']).optional(),
  order: ascDescOrder.optional(),
  states: campaignStateSchema.optional(),
  // Multi-select campaign-state filter (Contacts page). Mirrors `candidateStatuses`:
  // comma-joined in the URL, parsed to an array, and narrowed to valid states.
  cadenceStates: z
    .string()
    .or(z.array(z.string()))
    .transform(strToArrayOrUndefined)
    .transform((arr) => arr?.filter((s): s is CampaignState => campaignStateSchema.safeParse(s).success))
    .optional(),
  // Enrolled-list scope on the campaign detail page. Absent = 'active' (the
  // default view shows each contact's one live cadence); 'all' reveals every
  // non-deleted enrollment including finished/paused/past re-enrollments.
  enrollment: z.enum(['active', 'all']).optional(),
  candidateStatus: candidateStatusSchema.optional(),
  // Campaign staging table order (Candidates tab). Absent = newest first.
  candidateSort: candidateSortSchema.optional(),
  // Campaign staging table narrowed to the people ONE saved auto-loader search
  // brought in — set by the "N people" link on a saved-search row.
  autoLoadSearchId: z.string().transform(strToIntOrUndefined).optional(),
  // Campaign staging table narrowed to one source — set by a per-source count
  // in the Screening section of the Audience & Loaders tab.
  candidateSource: candidateSourceSchema.optional(),
  // …and to people with / without a verdict — what splits one parked status into
  // "waiting to be checked" and "passed, waiting to be added".
  candidateVerdict: candidateVerdictSchema.optional(),
  // …and to the people one kind of decision was made about (a "who decided" count's people).
  candidateDecision: candidateDecisionSchema.optional(),
  // Multi-select candidate-status filter (superadmin unadvanced-candidates page).
  candidateStatuses: z
    .string()
    .or(z.array(z.string()))
    .transform(strToArrayOrUndefined)
    .transform((arr) => arr?.filter((s): s is CandidateStatusParam => candidateStatusSchema.safeParse(s).success))
    .optional(),
  orgIds: z.string().or(z.array(z.string())).transform(strToArrayOrUndefined).optional(),
  // Checked people on the superadmin Oracle list — drives the bulk "Remove
  // backgrounds" / "Trim cutouts" selection popup. Values are person public ids
  // (uuids), stored directly (not joinIds-encoded like contact selections).
  oraclePersonIds: z.string().or(z.array(z.string())).transform(strToArrayOrUndefined).optional(),
  selectedIds: z.string().or(z.array(z.string())).transform(strToArrayOrUndefined).optional(),
  selectedCandidateIds: z.string().or(z.array(z.string())).transform(strToArrayOrUndefined).optional(),
  // Dedicated selection param for the Campaign Switch staging table — kept
  // separate from `selectedCandidateIds` (template staging) so the two tables'
  // selection state can't collide.
  selectedSwitchCandidateIds: z.string().or(z.array(z.string())).transform(strToArrayOrUndefined).optional(),
  selectedCadenceIds: z.string().or(z.array(z.string())).transform(strToArrayOrUndefined).optional(),
  // selectedStepIds: z.string().or(z.array(z.string())).transform(strToArrayOrUndefined).optional(),
  selectedHubspotIds: z.string().or(z.array(z.string())).transform(strToArrayOrUndefined).optional(),
  selectedDuplicatesIds: z.string().or(z.array(z.string())).transform(strToArrayOrUndefined).optional(),
  selectedCadenceEventIds: z.string().or(z.array(z.string())).transform(strToArrayOrUndefined).optional(),
  // interactIds: z.string().or(z.array(z.string())).transform(strToArrayOrUndefined).optional(),
  stepId: z.string().or(z.array(z.string())).transform(strToArrayOrUndefined).optional(),
  editingStep: z.string().optional(),
  analyticsAggInterval: z
    .enum(['hour', 'day', 'week', 'month', 'quarter', 'year'] as const satisfies AnalyticsAggInterval[])
    .optional(),
  contactSection: contactSectionSchema.optional(),
  researchSection: researchSectionSchema.optional(),
  onboarding: onboardingTourNameSchema.optional(),
  analytics: analyticsListSchema.optional(),
  postLeadsType: postLeadsTypeSchema.optional(),
  postType: postTypeSchema.optional(),
  isNotLoggedIn: z.string().optional(),
  title: z.string().optional(),
  openToWork: z.string().optional(),
  hiring: z.string().optional(),
  connected: z.string().optional(),
  company: z.string().optional(),
  companyId: z.string().optional(),
  city: z.string().optional(),
  contactId: z.string().optional(),
  menu: z.string().transform(strToBoolOrUndefined).optional(),
  model: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  dateRange: z.string().optional(),

  executable: z.string().transform(strToBoolOrUndefined).optional(),

  redirect: z.string().optional(),
  companyName: z.string().optional(),

  tag: z.string().optional(),

  postId: z.string().optional(),

  action: z.string().optional(),
  // Delivery-status filter (superadmin notifications page).
  status: z.string().optional(),
  subject: templateSubject.optional(),
  // Auth action params present in firebase auth email links
  mode: z.string().optional(),
  oobCode: z.string().optional(),
  continueUrl: z.string().optional(),
  lang: z.string().optional(),
  stepsRunCount: z.string().transform(strToIntOrUndefined).optional(),
  stepsRunCountOperation: operationValueSchema.optional(),
  showHidden: z.string().transform(strToBoolOrUndefined).optional(),
  response: everRespondSchema.optional(),
  connectionAccepted: z.enum(['accepted', 'sent']).optional(),
  connectionType: z.enum(['all', 'external', 'poseidon']).optional(),
  section: z.string().optional(),
  sortBy: z.enum(['company', 'connected', 'lastInteraction', 'lastReceived', 'stepStateChanged']).optional(),
  sortDirection: ascDescOrder.optional(),
  viewMode: z.enum(['card', 'table']).optional(),
  allCollapsed: z.string().transform(strToBoolOrUndefined).optional(),
  groupedByCompany: z.string().transform(strToBoolOrUndefined).optional(),
  resultType: z.enum(['connected', 'responded', 'fresh_response', 'todo']).optional(),
  recentlyConnected: z.string().transform(strToBoolOrUndefined).optional(),
  // Contacts page: restrict to contacts enrolled in at least one campaign.
  inCampaign: z.string().transform(strToBoolOrUndefined).optional(),
  messagesSentMax: z.enum(['1', '2', '3', '4+']).optional(),
  tab: z.enum(['review', 'queue']).optional(),
  replyFilter: replyFilterSchema.optional(),
  queueFilter: queueFilterSchema.optional(),
  // Emails Queued page: `all` shows every not-yet-sent email step (including
  // far-future ones); absent = the default `next` view (one row per active
  // campaign — its soonest upcoming email).
  queuedScope: z.enum(['next', 'all']).optional(),
  view: z.enum(['active', 'archived']).optional(),
  // Sequence detail page: which canvas is shown. Absent = the authored sequence editor (default);
  // `?journey=full` deep-links to the COMPLETE contact-journey graph (who's on each node); `?journey=people`
  // deep-links to the flat list of everyone enrolled with their status.
  // `?journey=audience` is the sequence's Audience & Loaders view (plan group E).
  journey: z.enum(['full', 'people', 'audience']).optional(),
  templateSort: z.enum(['created', 'active', 'enrolled', 'response_rate', 'connection_rate', 'last_run']).optional(),
  templateActivity: z.enum(['active', 'idle', 'stale', 'no_activity']).optional(),
  hasUsers: z.string().optional(),
  // Superadmin user filters
  teamCount: z.enum(['noTeam', 'oneTeam', 'multipleTeams']).optional(),
  /** Superadmin users / teams lists: put the least active first (for culling). */
  activitySort: z.enum(['leastActive']).optional(),
  verified: z.enum(['true', 'false']).optional(),
  // Superadmin Oracle explorer: which entity type to browse (default 'people')
  oracleTab: z.enum(['people', 'orgs']).optional(),
  // Superadmin Oracle people filter: '1' → show only people missing a nobg cutout
  missingNobg: z.enum(['1']).optional(),
  // Public id of an oracle person, used as the [personId] path param on the
  // superadmin Oracle person detail page (/superadmin/oracle/[personId]).
  personId: z.string().optional(),
  // Superadmin /superadmin/aiUsage view — ai* prefixed so they never collide with the global
  // from/to/model/teamId/userId keys. Absent = that control's default; `aiUsageUrlState.ts`
  // maps these to and from the page's state.
  aiPeriod: z.string().refine(isAiUsagePeriod).optional(),
  aiFrom: z.string().optional(),
  aiTo: z.string().optional(),
  aiProviders: z.string().or(z.array(z.string())).transform(strToArrayOrUndefined).optional(),
  aiTeams: z.string().or(z.array(z.string())).transform(strToArrayOrUndefined).optional(),
  aiFeatures: z.string().or(z.array(z.string())).transform(strToArrayOrUndefined).optional(),
  aiUsers: z.string().or(z.array(z.string())).transform(strToArrayOrUndefined).optional(),
  aiModels: z.string().or(z.array(z.string())).transform(strToArrayOrUndefined).optional(),
  aiSurfaces: z.string().or(z.array(z.string())).transform(strToArrayOrUndefined).optional(),
  aiGroupBy: z.enum(GROUP_BYS).optional(),
  // Providers collapsed in the Cost Explorer ('' = the Unknown group).
  aiCollapsed: z.string().or(z.array(z.string())).transform(strToArrayOrUndefined).optional(),
  aiChart: z.enum(CHART_VALUE_MODES).optional(),
  // Bad Numbers page: callable-state filter (un-callable / untried / already dialed).
  callable: z.enum(['none', 'untried', 'tried']).optional(),
  // RIA Search (Advisor Universe): filters as compact JSON so a list is one shareable URL; sort,
  // paging (offset + page size), and the person + firm open in the side panel.
  riaFilters: z.string().optional(),
  riaSort: z.enum(['fit', 'warm', 'aum', 'moved', 'connections', 'name']).optional(),
  riaOffset: z.string().transform(strToIntOrUndefined).optional(),
  riaLimit: z.string().transform(strToIntOrUndefined).optional(),
  riaFirm: z.string().optional(),
  riaPerson: z.string().transform(strToIntOrUndefined).optional(),
});

export type SearchParams = z.infer<typeof searchParamsSchema>;
export type SetSearchParams = SearchParams | ((old: SearchParams) => SearchParams);
/**
 * How a URL change lands in browser history. The default replaces the current
 * entry: filters, sorts and the tab bar adjust the view you are on, and Back
 * should leave the page, not undo them one by one. `push` is for a control IN
 * the page that takes you somewhere else (a count that opens another tab's
 * table) — you did not ask to move, so Back must bring you back.
 */
export type SetSearchOptions = { history?: 'replace' | 'push' };
export type SetSearchFn = (dataOrFn: SetSearchParams, options?: SetSearchOptions) => void;
export type UpdateSearchFn = (data: SearchParams, options?: SetSearchOptions) => void;

export type SearchParamsResultType = SearchParams['resultType'];

/**
 * Strips pagination params (offset, limit) from query object.
 * Use this when creating breadcrumb links to parent pages to avoid preserving
 * pagination state that doesn't apply to the destination page.
 */
export function stripPaginationParams<T extends Record<string, unknown>>(query: T): Omit<T, 'offset' | 'limit'> {
  return omit(query, ['offset', 'limit']);
}

export const useRouterQuery = (): [SearchParams, SetSearchFn, UpdateSearchFn] => {
  const router = useRouter();
  const query = router.query;

  const search = React.useMemo(() => {
    const parsedQuery = searchParamsSchema.safeParse(query);

    if (!parsedQuery.success) {
      // Log validation errors in development
      if (env.NODE_ENV === 'development') {
        console.warn('⚠️ Query parameter validation failed:', {
          errors: parsedQuery.error.errors,
          url: router.asPath,
          query: query,
        });
      }

      // Try to extract valid fields by parsing individually
      // This prevents one bad parameter from breaking everything
      const partialQuery: Partial<SearchParams> = {};
      for (const [key, value] of Object.entries(query)) {
        try {
          // Try to parse just this field
          const fieldSchema = searchParamsSchema.shape[key as keyof typeof searchParamsSchema.shape];
          if (fieldSchema) {
            const result = fieldSchema.safeParse(value);
            if (result.success) {
              partialQuery[key as keyof SearchParams] = result.data as never;
            }
          }
        } catch {
          // Skip invalid fields silently
        }
      }

      return partialQuery as SearchParams;
    }

    return parsedQuery.data;
  }, [query, router.asPath]);

  // Hold the latest parsed search + router in a ref so the setters below can
  // read current values without closing over them. This keeps setSearch /
  // updateSearch referentially STABLE across query changes. Consumers that
  // memoize handlers on these setters (e.g. the results list's per-row
  // callbacks) would otherwise get a new handler ref on every navigation,
  // re-rendering entire memoized lists on every click. `search` (the first
  // tuple element) stays reactive — only the setters become stable.
  const latest = React.useRef({ search, router });
  latest.current = { search, router };

  const setSearch = React.useCallback(async (dataOrFn: SetSearchParams, options?: SetSearchOptions) => {
    const { search: currentSearch, router: currentRouter } = latest.current;
    const data = typeof dataOrFn === 'function' ? dataOrFn(currentSearch) : dataOrFn;
    const url = {
      query: omitBy({ ...currentRouter.query, ...data }, (v) => v === false || v === undefined),
    };
    await (options?.history === 'push' ? currentRouter.push(url) : currentRouter.replace(url));
  }, []);
  const updateSearch = React.useCallback(
    (d: Partial<SearchParams>, options?: SetSearchOptions) => setSearch((s) => Object.assign({}, s, d), options),
    [setSearch],
  );

  return [search, setSearch, updateSearch];
};
